import cx from 'classnames';

import MarkDownRender from '~/components/Markdown/MarkDownRender';
import Typography from '~/components/Typography';
import type { TestimonialsSection as TestimonialsSectionType } from '~/interfaces/landing_page';

import Testimonial from './Testimonial';

interface Props {
  isUpgradePage?: boolean;
  testimonialsSection: TestimonialsSectionType;
  testimonials: any[];
  redirectActionChild?: React.ReactNode;
}

// This function is used to organize the list of testimonials based on the order of the testimonialsOrder array
const orderTestimonials = (listOfIds: string[], listOfTestimonials: any[]) => {
  const list1 = listOfTestimonials.filter((obj) => listOfIds.includes(obj.testimonial_id));
  const list2 = listOfTestimonials.filter((obj) => !listOfIds.includes(obj.testimonial_id));

  const sortedList1 = list1.sort((a, b) => listOfIds.indexOf(a.testimonial_id) - listOfIds.indexOf(b.testimonial_id));

  return [...sortedList1, ...list2];
};

const TestimonialsSection = ({
  isUpgradePage = false,
  testimonialsSection,
  testimonials,
  redirectActionChild,
}: Props) => {
  const organizedTestimonials = orderTestimonials(testimonialsSection?.testimonials, testimonials);

  return (
    <div
      style={{
        backgroundColor: testimonialsSection?.bg_color,
      }}
      className={cx({
        'flex flex-col p-16 px-4 pb-28': true,
      })}
    >
      <div className="mx-auto w-full max-w-5xl">
        <div className="mb-2 flex w-full flex-col items-center space-y-2">
          {testimonialsSection?.overline && (
            <Typography
              as="span"
              size={testimonialsSection?.overline_font_size}
              weight="bold"
              fontFamily={testimonialsSection?.overline_font_family}
              color={testimonialsSection?.overline_color}
              className={cx({
                'w-full': true,
                'text-center': testimonialsSection?.text_align === 'center',
                'text-left': testimonialsSection?.text_align === 'left',
                'text-right': testimonialsSection?.text_align === 'right',
              })}
            >
              <MarkDownRender>{testimonialsSection?.overline}</MarkDownRender>
            </Typography>
          )}
          <Typography
            as="h1"
            size={testimonialsSection?.headline_font_size}
            weight="bold"
            color={testimonialsSection?.headline_color}
            fontFamily={testimonialsSection?.headline_font_family}
            className={cx({
              'w-full': true,
              'text-center': testimonialsSection?.text_align === 'center',
              'text-left': testimonialsSection?.text_align === 'left',
              'text-right': testimonialsSection?.text_align === 'right',
            })}
          >
            <MarkDownRender>{testimonialsSection?.headline}</MarkDownRender>
          </Typography>
        </div>

        <div
          className={cx({
            'mt-12 flex flex-wrap gap-8': true,
            'justify-center': testimonialsSection?.text_align === 'center',
            'justify-start': testimonialsSection?.text_align === 'left',
            'justify-end': testimonialsSection?.text_align === 'right',
          })}
        >
          {organizedTestimonials?.map((testimonial) => {
            return (
              <Testimonial
                key={testimonial.id}
                message={testimonial.message}
                pictureUrl={testimonial.picture_url}
                name={testimonial.name}
                description={testimonial.description}
                testimonialsSection={testimonialsSection}
              />
            );
          })}
        </div>

        {isUpgradePage && redirectActionChild}
      </div>
    </div>
  );
};

export default TestimonialsSection;
